import type Phaser from "phaser";
import { hexToColor } from "../../../data/infinite-war-catalog";
import { t } from "../../../data/strings";
import {
  cardDef,
  soulResidueCountFor,
  teamCount,
  type InfiniteWarState,
} from "../infinite-war-rules";
import { BOARD, DEMIGOD_BAR, VIEW } from "./layout";

export type DemigodBarsHost = {
  statusGfx: Phaser.GameObjects.Graphics;
  northHpText: Phaser.GameObjects.Text;
  southHpText: Phaser.GameObjects.Text;
  northMeta: Phaser.GameObjects.Text;
  southMeta: Phaser.GameObjects.Text;
};

export function drawDemigodBars(
  host: DemigodBarsHost,
  state: InfiniteWarState,
): void {
  const north = teamCount(state, "north");
  const south = teamCount(state, "south");
  const northVeil = soulResidueCountFor(state, "north");
  const southVeil = soulResidueCountFor(state, "south");
  const decree = state.lastAiDecision.card
    ? t(cardDef(state.lastAiDecision.card).labelKey)
    : t("iw.ai.watching");
  const northGod = state.demigods.north;
  const southGod = state.demigods.south;
  const barX = BOARD.x + DEMIGOD_BAR.insetX;
  const barW = BOARD.w - DEMIGOD_BAR.insetX * 2;
  const g = host.statusGfx;
  g.clear();

  g.fillStyle(hexToColor(VIEW.colors.panelNorth), 0.88).fillRoundedRect(
    BOARD.x + 6,
    BOARD.y + 4,
    BOARD.w - 12,
    52,
    8,
  );
  g.fillStyle(hexToColor(VIEW.colors.hpBg), 1).fillRoundedRect(
    barX,
    DEMIGOD_BAR.northY,
    barW,
    DEMIGOD_BAR.h,
    4,
  );
  const northRatio = Math.max(0, northGod.hp / northGod.maxHp);
  g.fillStyle(hexToColor(VIEW.colors.hpNorth), 1).fillRoundedRect(
    barX,
    DEMIGOD_BAR.northY,
    barW * northRatio,
    DEMIGOD_BAR.h,
    4,
  );
  g.lineStyle(1, hexToColor(VIEW.colors.lineNorth), 0.95).strokeRoundedRect(
    barX,
    DEMIGOD_BAR.northY,
    barW,
    DEMIGOD_BAR.h,
    4,
  );
  host.northHpText.setText(
    t("iw.hud.hp", {
      hp: Math.max(0, northGod.hp).toFixed(0),
      max: northGod.maxHp.toFixed(0),
    }),
  );
  host.northMeta.setText(
    t("iw.hud.northMeta", {
      souls: String(north),
      veil: String(northVeil),
      decree,
    }),
  );

  g.fillStyle(hexToColor(VIEW.colors.panelSouth), 0.88).fillRoundedRect(
    BOARD.x + 6,
    DEMIGOD_BAR.southY - 20,
    BOARD.w - 12,
    36,
    8,
  );
  g.fillStyle(hexToColor(VIEW.colors.hpBg), 1).fillRoundedRect(
    barX,
    DEMIGOD_BAR.southY,
    barW,
    DEMIGOD_BAR.h,
    4,
  );
  const southRatio = Math.max(0, southGod.hp / southGod.maxHp);
  g.fillStyle(hexToColor(VIEW.colors.hpSouth), 1).fillRoundedRect(
    barX,
    DEMIGOD_BAR.southY,
    barW * southRatio,
    DEMIGOD_BAR.h,
    4,
  );
  g.lineStyle(1, hexToColor(VIEW.colors.lineSouth), 0.95).strokeRoundedRect(
    barX,
    DEMIGOD_BAR.southY,
    barW,
    DEMIGOD_BAR.h,
    4,
  );
  host.southHpText.setText(
    t("iw.hud.hp", {
      hp: Math.max(0, southGod.hp).toFixed(0),
      max: southGod.maxHp.toFixed(0),
    }),
  );
  // Selected card name belongs in the detail panel (HAND_UI.detailY), not here.
  host.southMeta.setText(
    t("iw.hud.southMeta", {
      souls: String(south),
      veil: String(southVeil),
    }),
  );
}

export function createDemigodBarTexts(scene: Phaser.Scene): {
  northHpText: Phaser.GameObjects.Text;
  southHpText: Phaser.GameObjects.Text;
  northMeta: Phaser.GameObjects.Text;
  southMeta: Phaser.GameObjects.Text;
} {
  return {
    northHpText: scene.add
      .text(270, DEMIGOD_BAR.northY + DEMIGOD_BAR.h / 2, "", {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "12px",
        color: "#eadcff",
      })
      .setOrigin(0.5)
      .setDepth(12),
    southHpText: scene.add
      .text(270, DEMIGOD_BAR.southY + DEMIGOD_BAR.h / 2, "", {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "12px",
        color: "#fff1ca",
      })
      .setOrigin(0.5)
      .setDepth(12),
    northMeta: scene.add
      .text(270, DEMIGOD_BAR.northY + DEMIGOD_BAR.h + 14, "", {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "11px",
        color: "#c4b5e8",
      })
      .setOrigin(0.5)
      .setDepth(11),
    southMeta: scene.add
      .text(270, DEMIGOD_BAR.southY - 18, "", {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "11px",
        color: "#e8d7a8",
      })
      .setOrigin(0.5)
      .setDepth(11),
  };
}
